































import Vue, { PropType } from "vue";
import { ApiResponse } from "@/api/types";
import { Chart } from "chart.js";
import Heading from "@/components/Atoms/Typography/Heading/index.vue";
import ChartCard from "@/components/Pages/CompanyDetail/Atoms/ChartCard/index.vue";
import CompareCompanyChartLegend from "../../Molecules/CompareCompanyChartLegend/index.vue";
// @ts-ignore
import { customDraw } from "@/components/Pages/CompanyDetail/Molecules/FavoriteChart/roundedBar";
// @ts-ignore
import { removeOverhangLine } from "@/helpers/chartPlugins";
import {
  grayscale90,
  grayscale60,
  line10,
  grayscale10
} from "@/assets/styles/vars";
import { compareCompanyColors } from "@/helpers/company-detail-helper";

const getLatestYear = (favoriteGraph: ApiResponse.FavoriteGraph[]) => {
  const favoriteGraphYears = favoriteGraph.map(e => e.graduate_year);
  return Math.max(...favoriteGraphYears);
};

export interface ExChartChartConfiguration extends Chart.ChartConfiguration {
  options: Chart.ChartOptions & {
    cornerRadius?: number;
  };
}

export interface Data {
  bindTo: string;
  chart: Chart | null;
  selectedYear: number;
}
export interface Computed {
  latestYear: number;
  chartData: Chart.ChartData;
  noChartData: boolean;
}
export interface Props {
  compareFavorites: {
    label: string;
    favoriteGraph: ApiResponse.FavoriteGraph[];
  }[];
}
export interface Methods {
  generateChart: () => void;
  customDrawInit: () => void;
  handleChangeSelectdYear: (value: string) => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "FavoriteCompare",
  components: {
    Heading,
    ChartCard,
    CompareCompanyChartLegend
  },
  props: {
    compareFavorites: {
      type: Array as PropType<Props["compareFavorites"]>,
      required: true
    }
  },
  data() {
    return {
      bindTo: "favoriteChart",
      chart: null,
      selectedYear: getLatestYear(this.compareFavorites[0].favoriteGraph) - 2
    };
  },
  computed: {
    latestYear() {
      return getLatestYear(this.compareFavorites[0].favoriteGraph);
    },
    chartData() {
      const labels = this.compareFavorites[0].favoriteGraph[0].graph_items.map(
        graphItem => `${graphItem.month}月`
      );

      const datasets = this.compareFavorites.map(({ favoriteGraph }, index) => {
        const targetFavorite = favoriteGraph.find(
          graphData => graphData.graduate_year === this.selectedYear
        );

        return {
          hidden: !targetFavorite,
          label: String(targetFavorite?.graduate_year).slice(-2),
          barThickness: 20,
          data: targetFavorite?.graph_items.map(graphItem =>
            Math.round(graphItem.favorites_count)
          ),
          pointRadius: 4,
          borderWidth: 2,
          pointBackgroundColor: compareCompanyColors[index].color,
          borderColor: compareCompanyColors[index].color,
          fill: false,
          lineTension: 0
        };
      });

      return { labels, datasets };
    },
    noChartData() {
      const datasets: Chart.ChartDataSets[] = this.chartData.datasets!;
      return datasets.every(dataset => dataset.hidden);
    }
  },
  created() {
    this.customDrawInit();
  },
  mounted() {
    this.generateChart();
    this.$watch("chartData", () => this.generateChart());
  },
  methods: {
    generateChart() {
      if (this.chart) this.chart.destroy();
      const canvas = document.getElementById(this.bindTo) as HTMLCanvasElement;
      const ctx = canvas && canvas.getContext("2d");
      if (!ctx) return;

      const chartConfig: ExChartChartConfiguration = {
        type: "line",
        plugins: [removeOverhangLine],
        data: this.chartData,
        options: {
          maintainAspectRatio: false,
          cornerRadius: 2,
          legend: {
            display: false
          },
          layout: { padding: 0 },
          tooltips: {
            titleFontColor: grayscale60,
            titleMarginBottom: 4.8,
            titleFontStyle: "400",
            bodyFontStyle: "700",
            callbacks: {
              title: ([tooltipItems]) => {
                const item = this.compareFavorites[0].favoriteGraph[
                  tooltipItems.datasetIndex!
                ].graph_items[tooltipItems.index!];
                return `${item.year}年${item.month}月`;
              },
              labelTextColor: tooltipItems => {
                return compareCompanyColors[tooltipItems.datasetIndex!].color;
              },
              label: tooltipItems => {
                return String(tooltipItems.value);
              }
            },
            position: "nearest",
            backgroundColor: grayscale10,
            displayColors: false,
            borderColor: line10,
            borderWidth: 1
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  zeroLineColor: grayscale90,
                  color: grayscale10
                },
                ticks: {
                  fontSize: 12,
                  fontColor: grayscale90
                }
              }
            ],
            yAxes: [
              {
                gridLines: {
                  zeroLineColor: grayscale90,
                  offsetGridLines: false
                },
                ticks: {
                  beginAtZero: true,
                  suggestedMin: 0,
                  fontSize: 12,
                  fontColor: grayscale90
                }
              }
            ]
          }
        }
      };

      this.chart = new Chart(ctx, chartConfig);
    },
    handleChangeSelectdYear(value) {
      this.selectedYear = Number(value);
    },
    customDrawInit() {
      // @ts-ignore
      Chart.elements.Rectangle.prototype.draw = customDraw;
    }
  }
});
